import { sanitizeCountryCode } from "@expert/common-ui";
import { CopyIcon } from "@expert/common-ui/assets";
import { CopyButton as Copy, Flex, Group, Modal, Pill, Stack, Text } from "@mantine/core";
import { type JSX, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";
import { ScheduleCallbackForm, WrappingCountdownText } from "@/call-controls";
import { Warning } from "@/common-ui";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useCallbacksRemaining, useSession } from "@/sdk";
import { CustomerDroppedNotification } from "../components/CustomerDroppedNotification";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import classes from "./EditSessionSummaryModal.module.css";
import { SessionSummaryForm } from "./SessionSummaryForm";
import { CALLBACK_CUTOFF } from "./utils";

interface EditSessionSummaryModalProps {
    caseNumber: string | undefined;
    endSession: (reason: "AgentTerminatedWrapping" | "WrappingTimeExpired") => Promise<void>;
    onDismiss: ({ checkSummary }: { checkSummary?: boolean }) => void;
    loading: boolean;
    simplifiedSummary: boolean;
    validAccountNumber: boolean;
    sendingSummary: boolean;
    onExpired: () => Promise<void> | void;
    opened: boolean;
}

export function EditSessionSummaryModal({
    endSession,
    onDismiss,
    validAccountNumber = false,
    loading,
    opened,
    onExpired,
    simplifiedSummary,
    sendingSummary,
    caseNumber,
}: EditSessionSummaryModalProps): JSX.Element | null {
    const [view, setView] = useState<"edit-summary" | "schedule-callback">("edit-summary");
    const { wrappingState } = useSession();
    const { callbacksRemaining, canCallback } = useCallbacksRemaining();
    const {
        isCustomerDroppedNotificationEnabled,
        isInternationalPhoneEnabled = false,
        defaultCountry = "US",
    } = usePartnerFeatureConfigured();

    const sanitizedDefaultCountry = sanitizeCountryCode(defaultCountry);

    const customerEndedCall = useSessionSummaryStore((state) => state.customerEndedCall);
    const showCallEndedBanner = customerEndedCall && isCustomerDroppedNotificationEnabled;

    useEffect(() => setView("edit-summary"), [opened]);

    function getTimerSeconds() {
        return wrappingState?.expirationTimestamp ? wrappingState.expirationTimestamp + 20 * 1000 : Date.now();
    }

    const { totalSeconds } = useTimer({
        expiryTimestamp: new Date(getTimerSeconds()),
        autoStart: !simplifiedSummary,
        onExpire: () => void onExpired(),
    });

    /** Prevent mantine's attempt to close the modal when the outside overlay is clicked */
    const interceptClose = () => null;
    const callbackScheduleHandler = () => {
        onDismiss({ checkSummary: false });
    };

    const reachedCallbackCutoff = totalSeconds <= CALLBACK_CUTOFF;
    return (
        <Modal
            centered
            classNames={{
                inner: classes.modalInner,
                overlay: classes.modalOverlay,
                content: classes.modalContent,
            }}
            onClose={interceptClose}
            opened={opened}
            transitionProps={{
                transition: "pop",
            }}
            overlayProps={{
                backgroundOpacity: 0.1,
            }}
            size="32rem"
            withCloseButton={false}
            title={showCallEndedBanner ? <CustomerDroppedNotification /> : null}
            styles={{
                title: {
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flex: 1,
                    width: "100%",
                    minHeight: "none",
                },
                header: {
                    margin: 0,
                    display: "flex",
                    flex: 1,
                    width: "100%",
                    minHeight: "none",
                    marginBottom: "var(--mantine-spacing-xs)",
                },
                body: {
                    padding: "var(--mb-padding, var(--mantine-spacing-md))",
                    overflowX: "hidden",
                    ...(showCallEndedBanner ? { paddingTop: 0 } : { paddingTop: "var(--mantine-spacing-md)" }),
                },
            }}
        >
            <Flex id="modal-heading" align="center" justify="space-between" mb="md">
                <Stack gap={0}>
                    {!caseNumber ? (
                        <Text className={classes.modalTitle} fw={"normal"} fz="md">
                            {view === "schedule-callback" ? "Schedule callback" : "Session summary"}
                        </Text>
                    ) : (
                        <Copy value={caseNumber}>
                            {({ copied, copy }) => (
                                <Group onClick={copy} gap={5} style={{ cursor: "pointer" }}>
                                    <Text
                                        className={classes.modalTitle}
                                        fw={!caseNumber ? "normal" : "bold"}
                                        fz="md"
                                        w="max-content"
                                        size="sm"
                                    >
                                        Summary for Case #{copied ? " copied!" : caseNumber}
                                    </Text>
                                    <Group gap={0} mt={-2}>
                                        <CopyIcon width="12" stroke="var(--mantine-color-gray-6)" />
                                    </Group>
                                </Group>
                            )}
                        </Copy>
                    )}
                    {!canCallback && (
                        <Text size="xs" c="warning.5">
                            You’ve reached the callback limit.
                        </Text>
                    )}
                </Stack>
                {!simplifiedSummary && (
                    <Pill classNames={{ root: classes.pillRoot, label: classes.pillLabel }} fz="xxs" size="xs">
                        <WrappingCountdownText
                            appendUnit={false}
                            format="m:ss"
                            ff="monospace"
                            fw="var(--mantine-font-weight-heavy)"
                            size="xxs"
                            totalSeconds={totalSeconds}
                        />
                        {" sec"}
                    </Pill>
                )}
            </Flex>
            {view === "schedule-callback" ? (
                <>
                    <ScheduleCallbackForm
                        onBackClick={() => setView("edit-summary")}
                        afterSubmitClickDefault={callbackScheduleHandler}
                        reachedCallbackCutoff={reachedCallbackCutoff}
                        defaultCountry={sanitizedDefaultCountry}
                        isInternationalPhoneEnabled={isInternationalPhoneEnabled}
                    />
                    {reachedCallbackCutoff && (
                        <Group pt="3xs" gap={5} justify="center">
                            <Warning width="10" fill="var(--mantine-color-red-3)" />
                            <Text pl={0} fz="xs" c="red.3">
                                Sorry, there’s not enough time to schedule a callback
                            </Text>
                        </Group>
                    )}
                </>
            ) : (
                <SessionSummaryForm
                    simplifiedSummary={simplifiedSummary}
                    validAccountNumber={validAccountNumber}
                    callbackRemaining={callbacksRemaining}
                    sendingSummary={sendingSummary}
                    setView={setView}
                    endSession={endSession}
                    loading={loading}
                />
            )}
        </Modal>
    );
}
